import type { CostContractStatus, CostContractView, CostContractViewValues } from '../types'

export type ViewRow =
  | { kind: 'contract'; id: string; label: string; counterparty: string; status: CostContractStatus; values: CostContractViewValues; expandable: boolean; expanded: boolean }
  | { kind: 'contractItem'; id: string; path: string; label: string; values: CostContractViewValues }
  | { kind: 'section' }
  | { kind: 'node'; id: string; depth: number; label: string; isGroup: boolean; collapsed: boolean; values: CostContractViewValues }
  | { kind: 'total'; values: CostContractViewValues }
  | { kind: 'empty'; which: 'contracts' | 'uncontracted' }

/** Flat, render-ready rows. `uncontracted` arrives in tree pre-order with parent links; a collapsed group hides its subtree. */
export function contractViewRows(view: CostContractView, expanded: Set<string>, collapsed: Set<string>): ViewRow[] {
  const rows: ViewRow[] = []
  if (view.contracts.length === 0) rows.push({ kind: 'empty', which: 'contracts' })
  for (const c of view.contracts) {
    const open = expanded.has(c.id) && c.items.length > 0
    rows.push({
      kind: 'contract', id: c.id, label: c.number ? `${c.number} · ${c.name}` : c.name, counterparty: c.counterpartyName,
      status: c.status, values: c.values, expandable: c.items.length > 0, expanded: open,
    })
    if (open) c.items.forEach((i) => rows.push({ kind: 'contractItem', id: `${c.id}:${i.nodeId}`, path: i.path, label: i.name, values: i.values }))
  }
  rows.push({ kind: 'section' })
  if (view.uncontracted.length === 0) rows.push({ kind: 'empty', which: 'uncontracted' })
  const parents = new Map(view.uncontracted.map((n) => [n.id, n.parentId]))
  const depthOf = (id: string): number => { let d = 0; for (let p = parents.get(id); p; p = parents.get(p)) d += 1; return d }
  const hidden = (id: string): boolean => { for (let p = parents.get(id); p; p = parents.get(p)) if (collapsed.has(p)) return true; return false }
  for (const n of view.uncontracted) {
    if (hidden(n.id)) continue
    rows.push({ kind: 'node', id: n.id, depth: depthOf(n.id), label: n.name, isGroup: n.kind === 'GROUP', collapsed: collapsed.has(n.id), values: n.values })
  }
  rows.push({ kind: 'total', values: view.totals })
  return rows
}
